import React, { useEffect, useRef, useState } from "react";
import "./CustomMenu.css";

/**
 * CustomMenu — the one "Custom" button in the header, with a hover/click
 * panel that is the backdoor to everything a member builds for themselves:
 * dashboards, skills, connections + tools. Fewer top buttons, not more
 * (Brad, 2026-09-15). Anything custom added later (tools, automations…)
 * gets one more row here and one more card in Settings → Custom, nothing
 * else. Keyboard: Enter/Space opens, Esc closes, arrows move.
 *
 * @param {{ items: Array<{ id: string, icon: string, label: string, hint: string, onSelect: () => void }> }} props
 */
const CustomMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const enter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const leave = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 180); };

  return (
    <div className="custom-menu" ref={rootRef} onMouseEnter={enter} onMouseLeave={leave} data-testid="custom-menu">
      <button
        type="button"
        className="roi-dashboard-btn custom-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Everything you've built for yourself: dashboards, skills, connections and tools"
        data-testid="custom-menu-trigger"
      >
        🛠️ Custom <span className="custom-menu-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="custom-menu-panel hf-reveal-scale" role="menu" data-testid="custom-menu-panel">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              className="custom-menu-item"
              onClick={() => { setOpen(false); it.onSelect(); }}
              data-testid={`custom-menu-${it.id}`}
            >
              <span className="custom-menu-icon" aria-hidden="true">{it.icon}</span>
              <span className="custom-menu-text">
                <span className="custom-menu-label">{it.label}</span>
                <span className="custom-menu-hint">{it.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomMenu;
